import type { FC, ReactNode } from 'react'

import Image from 'next/image'

import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { useScrollLock } from '@/components/generals/smooth-scroll'
import { heroImages } from '@/constants/content'

/*
 * "The Index": the opening sequence. A black screen with a tall list of
 * project titles spinning like a slot machine until it rests on "Auren",
 * a 000 to 100 counter with a gold line, and three small portrait cards
 * stacked in the middle. The cards then lift off and fly to the exact spots
 * of the three equal hero portraits (growing into their tall shape on the
 * way) while the black splits into two panels that slide apart, up and down.
 *
 * The full version (about 3 seconds) plays on the first page load of a
 * session, later full page loads in the same session get a 1 second version,
 * and visitors who prefer reduced motion get a simple fade of the word.
 */

type Mode = 'full' | 'short' | 'reduced'
type Range = [number, number]

type Schedule = {
    labels: Range
    cardsIn: number
    cardStagger: number
    spin: Range
    uiOut: Range
    flight: number
    flightStagger: number
    flightDuration: number
    panels: Range
}

const FULL: Schedule = {
    labels: [0, 0.5],
    cardsIn: 0.15,
    cardStagger: 0.12,
    spin: [0.3, 2.15],
    uiOut: [2.15, 2.45],
    flight: 2.25,
    flightStagger: 0.07,
    flightDuration: 1.05,
    panels: [2.35, 3.4]
}

const SHORT: Schedule = {
    labels: [0, 0.15],
    cardsIn: 0,
    cardStagger: 0.04,
    spin: [0.05, 0.5],
    uiOut: [0.5, 0.64],
    flight: 0.55,
    flightStagger: 0.03,
    flightDuration: 0.52,
    panels: [0.58, 1.12]
}

const REDUCED = { wordIn: [0.1, 0.6] as Range, fadeOut: [0.9, 1.4] as Range, ready: 0.9, duration: 1.4 }

const SEEN_KEY = 'auren:intro-seen'
const FALLBACK_LIMIT_MS = 4300

const TITLES = ['Maison Tevre', 'Oriel Gallery', 'Kōra Health', 'Halden Launch', 'Rain Over Neon']
const ROWS = [...TITLES, ...TITLES, 'Auren']
const ROW_EM = 1.15

/** Hero portrait index for each card, and the order they lift off in. */
const STACK = [
    { rotate: -9, dx: -10, dy: 6, order: 1 },
    { rotate: 2, dx: 0, dy: 0, order: 0 },
    { rotate: 10, dx: 12, dy: 4, order: 2 }
]

const CARD_ORDER = [0, 2, 1]
const STACK_RADIUS = 6
const TARGET_RADIUS = 10

const clamp = (value: number) => Math.min(1, Math.max(0, value))
const progress = (t: number, [start, end]: Range) => (end <= start ? (t >= end ? 1 : 0) : clamp((t - start) / (end - start)))
const easeOut = (value: number) => 1 - Math.pow(1 - value, 3)
const easeOutQuart = (value: number) => 1 - Math.pow(1 - value, 4)
const easeInOut = (value: number) => (value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2)
const easeInOutQuart = (value: number) => (value < 0.5 ? 8 * Math.pow(value, 4) : 1 - Math.pow(-2 * value + 2, 4) / 2)
const lerp = (from: number, to: number, amount: number) => from + (to - from) * amount

type IntroState = {
    ready: boolean
    handoff: boolean
}

const IntroContext = createContext<IntroState>({ ready: true, handoff: false })

/** True once the opening sequence starts revealing the page (always true afterwards). */
export const useIntroReady = (): boolean => useContext(IntroContext).ready

/**
 * True while the opening cards are flying onto the hero portraits. The hero
 * then skips its own entrance so the cards can hand over seamlessly.
 */
export const useIntroHandoff = (): boolean => useContext(IntroContext).handoff

interface IntroProviderProps {
    children: ReactNode
}

export const IntroProvider: FC<IntroProviderProps> = ({ children }): ReactNode => {
    const [ready, setReady] = useState(false)
    const [handoff, setHandoff] = useState(false)
    const [active, setActive] = useState(true)
    const value = useMemo(() => ({ ready, handoff }), [ready, handoff])

    useScrollLock(active)

    return (
        <IntroContext.Provider value={value}>
            {children}

            {active && (
                <IndexLoader
                    onHandoff={() => setHandoff(true)}
                    onReady={() => setReady(true)}
                    onDone={() => {
                        setReady(true)
                        setHandoff(false)
                        setActive(false)
                    }}
                />
            )}
        </IntroContext.Provider>
    )
}

interface IndexLoaderProps {
    onHandoff: () => void
    onReady: () => void
    onDone: () => void
}

type Pose = { x: number, y: number, width: number, height: number, rotate: number, radius: number }
type Target = { x: number, y: number, width: number, height: number }

const IndexLoader: FC<IndexLoaderProps> = ({ onHandoff, onReady, onDone }): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)
    const topPanelRef = useRef<HTMLDivElement>(null)
    const bottomPanelRef = useRef<HTMLDivElement>(null)
    const uiRef = useRef<HTMLDivElement>(null)
    const labelsRef = useRef<HTMLDivElement>(null)
    const slotRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    const counterWrapRef = useRef<HTMLDivElement>(null)
    const counterRef = useRef<HTMLSpanElement>(null)
    const lineRef = useRef<HTMLSpanElement>(null)
    const stackRef = useRef<HTMLDivElement>(null)
    const cardRefs = useRef<Array<HTMLDivElement | null>>([])
    const wordRef = useRef<HTMLSpanElement>(null)
    const skipButtonRef = useRef<HTMLButtonElement>(null)
    const skipRequestedRef = useRef(false)
    const modeRef = useRef<Mode | null>(null)
    const callbacksRef = useRef({ onHandoff, onReady, onDone })

    callbacksRef.current = { onHandoff, onReady, onDone }

    useEffect(() => {
        const root = rootRef.current

        if (!root) return

        const fallback = root.getAnimations?.()[0]
        const fallbackTime = fallback?.currentTime

        if (typeof fallbackTime === 'number' && fallbackTime > FALLBACK_LIMIT_MS) {
            callbacksRef.current.onDone()
            return
        }

        root.style.animation = 'none'

        if (!modeRef.current) {
            let mode: Mode = 'full'

            try {
                if (window.sessionStorage.getItem(SEEN_KEY)) mode = 'short'
                window.sessionStorage.setItem(SEEN_KEY, '1')
            }
            catch {
                mode = 'full'
            }

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) mode = 'reduced'

            modeRef.current = mode
        }

        const mode = modeRef.current
        const schedule = mode === 'short' ? SHORT : FULL
        const landing = schedule.flight + schedule.flightStagger * (STACK.length - 1) + schedule.flightDuration
        const readyAt = mode === 'reduced' ? REDUCED.ready : landing
        const duration = mode === 'reduced' ? REDUCED.duration : Math.max(landing, schedule.panels[1]) + 0.1
        const skipTo = mode === 'reduced' ? REDUCED.ready : schedule.flight - 0.12
        const pace = mode === 'full' ? 0.75 : mode === 'short' ? 0.8 : 1
        let start = performance.now()
        let frame = 0
        let readyFired = false
        let handoffFired = false
        let lastCount = ''
        let lastPos = 0
        let lastTime = 0
        let stackCenter: { x: number, y: number } | null = null
        let baseWidth = 0
        let targets: Array<Target | null> | null = null

        const readTargets = () => {
            const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-intro-target]'))

            if (nodes.length < STACK.length) return null

            return STACK.map((_, index) => {
                const node = nodes.find(element => element.dataset.introTarget === String(index))

                if (!node) return null

                const rect = node.getBoundingClientRect()

                if (rect.width === 0 || rect.bottom < 0 || rect.top > window.innerHeight) return null

                return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, width: rect.width, height: rect.height }
            })
        }

        const placeCard = (card: HTMLDivElement, pose: Pose, opacity: number) => {
            card.style.width = `${pose.width.toFixed(2)}px`
            card.style.height = `${pose.height.toFixed(2)}px`
            card.style.transform = `translate3d(${(pose.x - pose.width / 2).toFixed(2)}px, ${(pose.y - pose.height / 2).toFixed(2)}px, 0) rotate(${pose.rotate.toFixed(3)}deg)`
            card.style.borderRadius = `${pose.radius.toFixed(2)}px`
            card.style.opacity = opacity.toFixed(3)
        }

        const renderReduced = (t: number) => {
            const word = wordRef.current

            if (word) word.style.opacity = String(easeOut(progress(t, REDUCED.wordIn)))

            root.style.opacity = String(1 - progress(t, REDUCED.fadeOut))

            if (skipButtonRef.current) skipButtonRef.current.style.opacity = t < REDUCED.ready ? '1' : '0'
        }

        const render = (t: number, s: Schedule) => {
            const uiVisible = 1 - easeInOut(progress(t, s.uiOut))

            if (uiRef.current) uiRef.current.style.opacity = uiVisible.toFixed(3)
            if (labelsRef.current) labelsRef.current.style.opacity = easeOut(progress(t, s.labels)).toFixed(3)

            /* Slot list: spins upward and settles on the last row, "Auren". */
            const spin = progress(t, s.spin)
            const position = easeOutQuart(spin) * (ROWS.length - 1)
            const delta = t - lastTime
            const speed = delta > 0 ? Math.abs(position - lastPos) / delta : 0

            lastPos = position
            lastTime = t

            if (slotRef.current) slotRef.current.style.opacity = easeOut(progress(t, [s.spin[0] - 0.2, s.spin[0] + 0.15])).toFixed(3)

            if (listRef.current) {
                listRef.current.style.transform = `translate3d(0, ${(-position * ROW_EM).toFixed(4)}em, 0)`
                listRef.current.style.filter = speed > 1 ? `blur(${Math.min(3.5, speed * 0.16).toFixed(2)}px)` : 'none'
            }

            /* Counter and the gold line beneath it. */
            const count = easeInOut(spin)
            const countText = String(Math.round(count * 100)).padStart(3, '0')

            if (counterWrapRef.current) counterWrapRef.current.style.opacity = easeOut(progress(t, [s.spin[0] - 0.2, s.spin[0] + 0.15])).toFixed(3)

            if (counterRef.current && countText !== lastCount) {
                lastCount = countText
                counterRef.current.textContent = countText
            }

            if (lineRef.current) lineRef.current.style.transform = `scaleX(${count.toFixed(4)})`

            /* Cards: stacked in the middle, then flown to the hero portraits. */
            const firstFlight = t >= s.flight

            if (!firstFlight || !stackCenter) {
                const stack = stackRef.current?.getBoundingClientRect()

                if (stack) stackCenter = { x: stack.left + stack.width / 2, y: stack.top + stack.height / 2 }
            }

            if (firstFlight && !targets) {
                const found = readTargets()

                targets = found && found.every(Boolean) ? found : STACK.map(() => null)

                if (targets.every(Boolean) && !handoffFired) {
                    handoffFired = true
                    callbacksRef.current.onHandoff()
                }
            }
            else if (firstFlight && targets && targets.every(Boolean)) {
                const next = readTargets()

                if (next && next.every(Boolean)) targets = next
            }

            const center = stackCenter ?? { x: window.innerWidth / 2, y: window.innerHeight / 2 }

            cardRefs.current.forEach((card, index) => {
                if (!card) return

                if (!baseWidth) baseWidth = card.offsetWidth

                const layout = STACK[index]
                const appear = easeOut(progress(t, [s.cardsIn + CARD_ORDER.indexOf(index) * s.cardStagger, s.cardsIn + CARD_ORDER.indexOf(index) * s.cardStagger + 0.55]))
                const flightStart = s.flight + layout.order * s.flightStagger
                const fly = easeInOutQuart(progress(t, [flightStart, flightStart + s.flightDuration]))
                const lift = progress(t, [flightStart, flightStart + s.flightDuration])
                const wobble = Math.sin(t * 2.2 + index * 1.7) * 2.4 * (1 - fly)

                const stackScale = lerp(0.72, 1, appear)

                const stackPose: Pose = {
                    x: center.x + layout.dx,
                    y: center.y + layout.dy + (1 - appear) * 46,
                    width: baseWidth * stackScale,
                    height: baseWidth * 1.25 * stackScale,
                    rotate: layout.rotate * appear + wobble,
                    radius: STACK_RADIUS
                }

                const target = targets?.[index]
                let pose = stackPose
                let opacity = appear

                if (target && baseWidth) {
                    const bump = 1 + Math.sin(Math.PI * Math.min(1, lift * 2)) * 0.06 * (1 - fly)

                    pose = {
                        x: lerp(stackPose.x, target.x, fly),
                        y: lerp(stackPose.y, target.y, fly) - Math.sin(Math.PI * lift) * 70,
                        width: lerp(stackPose.width, target.width, fly) * bump,
                        height: lerp(stackPose.height, target.height, fly) * bump,
                        rotate: lerp(stackPose.rotate, 0, fly),
                        radius: lerp(STACK_RADIUS, TARGET_RADIUS, fly)
                    }
                }
                else if (lift > 0) {
                    pose = {
                        ...stackPose,
                        y: stackPose.y - easeInOut(lift) * window.innerHeight * 0.55,
                        width: stackPose.width * (1 + lift * 0.4),
                        height: stackPose.height * (1 + lift * 0.4)
                    }
                    opacity = appear * (1 - easeInOut(lift))
                }

                placeCard(card, pose, opacity)
            })

            /* The black splits into two panels that slide up and down. */
            const open = easeInOutQuart(progress(t, s.panels))

            if (topPanelRef.current) topPanelRef.current.style.transform = `translate3d(0, ${(-open * 100).toFixed(3)}%, 0)`
            if (bottomPanelRef.current) bottomPanelRef.current.style.transform = `translate3d(0, ${(open * 100).toFixed(3)}%, 0)`

            if (skipButtonRef.current) {
                const visible = t < s.flight - 0.12

                skipButtonRef.current.style.opacity = visible ? String(progress(t, [0.15, 0.45])) : '0'
                skipButtonRef.current.style.pointerEvents = visible ? 'auto' : 'none'
            }
        }

        const loop = (now: number) => {
            let t = ((now - start) / 1000) * pace

            if (skipRequestedRef.current) {
                skipRequestedRef.current = false

                if (t < skipTo) {
                    start = now - (skipTo * 1000) / pace
                    t = skipTo
                }
            }

            if (mode === 'reduced') renderReduced(t)
            else render(t, schedule)

            if (!readyFired && t >= readyAt) {
                readyFired = true
                callbacksRef.current.onReady()
            }

            if (t >= duration) {
                callbacksRef.current.onDone()
                return
            }

            frame = requestAnimationFrame(loop)
        }

        if (mode === 'reduced') {
            if (uiRef.current) uiRef.current.style.display = 'none'
            if (labelsRef.current) labelsRef.current.style.display = 'none'
            cardRefs.current.forEach(card => {
                if (card) card.style.display = 'none'
            })
        }
        else if (wordRef.current) {
            wordRef.current.style.display = 'none'
        }

        frame = requestAnimationFrame(loop)

        return () => cancelAnimationFrame(frame)
    }, [])

    return (
        <div
            ref={rootRef}
            role='status'
            aria-label='Loading Auren'
            className='intro-fallback fixed inset-0 z-[92] overflow-hidden'
        >
            <div ref={topPanelRef} aria-hidden='true' className='absolute inset-x-0 top-0 h-[calc(50%_+_1px)] bg-background will-change-transform' />
            <div ref={bottomPanelRef} aria-hidden='true' className='absolute inset-x-0 bottom-0 h-[calc(50%_+_1px)] bg-background will-change-transform' />

            <div ref={labelsRef} aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 opacity-0'>
                <div className='shell flex h-18 items-center justify-between text-[11px] uppercase tracking-[0.22em] text-muted-foreground'>
                    <span>Auren, Portfolio 2026</span>
                    <span>London</span>
                </div>
            </div>

            <div ref={uiRef} aria-hidden='true' className='pointer-events-none absolute inset-0 flex items-center justify-center'>
                <div className='shell grid items-center justify-items-center gap-10 md:grid-cols-[1fr_auto_1fr] md:gap-14'>
                    <div
                        ref={slotRef}
                        className='relative h-[3.45em] overflow-hidden text-[clamp(1.9rem,4.4vw,3.9rem)] font-light leading-none tracking-[-0.05em] opacity-0 [mask-image:linear-gradient(to_bottom,transparent,#000_38%,#000_62%,transparent)] md:justify-self-end md:text-right'
                    >
                        <ul ref={listRef} className='pt-[1.15em] will-change-transform'>
                            {ROWS.map((row, index) => (
                                <li
                                    key={`${row}-${index}`}
                                    className={index === ROWS.length - 1 ? 'flex h-[1.15em] items-center justify-center text-foreground md:justify-end' : 'flex h-[1.15em] items-center justify-center whitespace-nowrap text-foreground/40 md:justify-end'}
                                >
                                    {row}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div ref={stackRef} className='aspect-[4/5] w-[clamp(84px,9vw,128px)]' />

                    <div ref={counterWrapRef} className='opacity-0 md:justify-self-start'>
                        <span ref={counterRef} className='block text-[clamp(3.25rem,8vw,7.5rem)] font-extralight leading-none tracking-[-0.06em] tabular-nums text-foreground'>
                            000
                        </span>

                        <span className='mt-3 block h-px w-full bg-white/10'>
                            <span ref={lineRef} className='block h-full origin-left scale-x-0 bg-gold' />
                        </span>
                    </div>
                </div>
            </div>

            {heroImages.map((image, index) => (
                <div
                    key={image.src}
                    ref={element => {
                        cardRefs.current[index] = element
                    }}
                    aria-hidden='true'
                    className='fixed left-0 top-0 aspect-[4/5] w-[clamp(84px,9vw,128px)] origin-center overflow-hidden rounded-[6px] bg-surface opacity-0 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] will-change-transform'
                    style={{ zIndex: index === 1 ? 3 : 2 }}
                >
                    <Image
                        src={image.src}
                        alt=''
                        fill
                        preload
                        quality={85}
                        sizes='(min-width: 768px) 32vw, 30vw'
                        className='object-cover'
                    />
                </div>
            ))}

            <span
                ref={wordRef}
                aria-hidden='true'
                className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[clamp(3rem,10vw,8rem)] font-light tracking-[-0.06em] text-foreground opacity-0'
            >
                Auren
            </span>

            <button
                ref={skipButtonRef}
                type='button'
                onClick={() => {
                    skipRequestedRef.current = true
                }}
                className='absolute bottom-6 right-[clamp(1.25rem,4vw,3rem)] z-10 py-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground opacity-0 transition-colors hover:text-gold'
            >
                Skip
            </button>
        </div>
    )
}
